import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Phone, CheckCircle, ArrowRight, MapPin, BookOpenText } from "@phosphor-icons/react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import QuoteForm from "@/components/QuoteForm";
import JobStories from "@/components/JobStories";
import { GoogleRatingBadge } from "@/components/GoogleReviews";
import { HAUL_BULLETS, PHONE_DISPLAY, PHONE_TEL } from "@/seoData";
import usePricing from "@/hooks/usePricing";
import { applyPrices } from "@/priceTokens";

export default function SeoLandingPage({ config: rawConfig }) {
  const pricing = usePricing();
  const config = applyPrices(rawConfig, pricing);
  const { h1, crumb, lead, leadHtml, seo, faqs = [], related = [], relatedTitle, benefitsTitle, neighborhoods = [], neighborhoodsTitle, sections = [], quoteVariant = "seo", serviceSlug, extra = null } = config;
  useEffect(() => { window.scrollTo(0, 0); }, [seo.canonical]);

  return (
    <div className="bg-white text-[#0A0A0A] overflow-x-hidden">
      <Seo {...seo} />
      <Header />

      {/* HERO */}
      <section className="px-5 lg:px-8 pt-28 sm:pt-44 lg:pt-52 pb-16 bg-[#0B1E45] text-white">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 text-[#5DB82F] font-display font-bold uppercase tracking-widest text-xs mb-4">
              <span className="h-px w-8 bg-[#5DB82F]" /> {crumb}
            </div>
            <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.98]">
              {h1}
            </h1>
            {leadHtml ? (
              <p className="mt-6 text-lg text-white/80 max-w-xl leading-relaxed [&_a]:text-[#7ED957] [&_a]:underline" dangerouslySetInnerHTML={{ __html: leadHtml }} />
            ) : (
              <p className="mt-6 text-lg text-white/80 max-w-xl leading-relaxed">{lead}</p>
            )}
            <GoogleRatingBadge />
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a href={`tel:${PHONE_TEL}`} data-testid="seo-call-button" className="flex items-center gap-2 bg-[#46A01E] hover:bg-[#3A8418] text-white font-display font-extrabold uppercase tracking-wide px-7 py-4 rounded-sm transition-colors">
                <Phone size={20} weight="fill" /> Call/Text {PHONE_DISPLAY}
              </a>
              <button onClick={() => document.getElementById("quote")?.scrollIntoView({ behavior: "smooth" })} data-testid="seo-quote-scroll" className="flex items-center gap-2 text-white font-display font-bold uppercase tracking-wide px-2 py-4 hover:text-[#5DB82F] transition-colors">
                Get a Free Quote <ArrowRight size={18} weight="bold" />
              </button>
            </div>
          </div>
          <div className="lg:col-span-5" id="quote">
            <div className="bg-white rounded-sm p-6 lg:p-8 border-t-4 border-[#46A01E] shadow-2xl text-[#0A0A0A]">
              <h2 className="font-display font-extrabold text-2xl tracking-tight text-[#15306E]">{quoteVariant === "commercial" ? "Get a Business Quote" : "Get a Free Quote"}</h2>
              <p className="text-sm text-[#52525B] mt-1 mb-5">60-second form. We'll text or call you right back.</p>
              <QuoteForm variant={quoteVariant} />
            </div>
          </div>
        </div>
      </section>

      {/* BENEFITS / WHAT WE HAUL */}
      <section className="px-5 lg:px-8 py-20 lg:py-24">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-display font-black tracking-tighter text-3xl lg:text-4xl text-[#15306E] max-w-2xl">
            {benefitsTitle}
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
            {HAUL_BULLETS.map((b) => (
              <div key={b} className="flex items-start gap-3 border border-[#E4E4E7] rounded-sm p-5">
                <CheckCircle size={24} weight="fill" className="text-[#46A01E] shrink-0 mt-0.5" />
                <span className="text-[#0A0A0A] font-medium">{b}</span>
              </div>
            ))}
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {["Licensed & Insured", "Upfront Pricing", "Same-Day Service", "Eco-Friendly Disposal"].map((t) => (
              <div key={t} className="bg-[#F4FAF0] border border-[#E4E4E7] rounded-sm px-5 py-4 font-display font-bold text-[#15306E] text-sm">{t}</div>
            ))}
          </div>
        </div>
      </section>

      {/* RICH CONTENT SECTIONS */}
      {sections.length > 0 && (
        <section className="px-5 lg:px-8 pb-4">
          <div className="max-w-3xl mx-auto space-y-10">
            {sections.map((s, i) => (
              <div key={i} data-testid={`content-section-${i}`}>
                <h2 className="font-display font-black tracking-tighter text-2xl lg:text-3xl text-[#15306E] mb-4">{s.h2}</h2>
                {(s.paras || []).map((p, j) => (
                  <p key={j} className="text-[#3F3F46] leading-relaxed text-[17px] mb-4">{p}</p>
                ))}
                {s.bullets && (
                  <ul className="space-y-2.5 mb-2">
                    {s.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2.5 text-[#3F3F46] text-[16px] leading-relaxed">
                        <CheckCircle size={18} weight="fill" className="text-[#46A01E] mt-1 shrink-0" /> {b}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {extra}

      {serviceSlug && <JobStories serviceSlug={serviceSlug} serviceName={crumb} />}

      {/* NEIGHBORHOODS */}
      {neighborhoods.length > 0 && (
        <section className={`px-5 lg:px-8 pb-4 ${extra ? "pt-12" : "-mt-8"}`}>
          <div className="max-w-7xl mx-auto">
            <h2 className="font-display font-extrabold text-xl text-[#15306E] mb-5">{neighborhoodsTitle}</h2>
            <div className="flex flex-wrap gap-2.5">
              {neighborhoods.map((n) => (
                <span key={n} className="flex items-center gap-1.5 bg-[#F4FAF0] border border-[#E4E4E7] px-3 py-2 rounded-sm text-sm text-[#15306E]">
                  <MapPin size={14} weight="fill" className="text-[#46A01E]" /> {n}
                </span>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      {faqs.length > 0 && (
        <section className="px-5 lg:px-8 py-20 lg:py-24 bg-[#F4F4F5]">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-display font-black tracking-tighter text-3xl lg:text-4xl text-[#15306E] mb-8">
              Frequently Asked Questions
            </h2>
            <div className="space-y-3">
              {faqs.map((f, i) => (
                <details key={i} className="group bg-white border border-[#E4E4E7] rounded-sm p-5" data-testid={`faq-${i}`}>
                  <summary className="cursor-pointer font-display font-bold text-[#15306E] list-none flex justify-between items-center">
                    {f.q}
                    <span className="text-[#46A01E] group-open:rotate-45 transition-transform text-xl leading-none">+</span>
                  </summary>
                  <p className="mt-3 text-[#52525B] leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* RELATED LINKS */}
      {related.length > 0 && (
        <section className="px-5 lg:px-8 py-16">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-display font-extrabold text-xl text-[#15306E] mb-6">{relatedTitle}</h2>
            <div className="flex flex-wrap gap-2.5">
              {related.map((r) => (
                <Link key={r.to} to={r.to} className="flex items-center gap-1.5 border border-[#E4E4E7] hover:border-[#46A01E] hover:text-[#46A01E] px-3 py-2 rounded-sm text-sm text-[#15306E] transition-colors">
                  {r.to.startsWith("/blog/") ? <BookOpenText size={14} weight="fill" className="text-[#46A01E]" /> : <MapPin size={14} weight="fill" className="text-[#46A01E]" />} {r.label}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="px-5 lg:px-8 pb-20">
        <div className="max-w-7xl mx-auto bg-[#15306E] rounded-sm p-10 lg:p-14 text-white text-center">
          <h2 className="font-display font-black tracking-tighter text-3xl lg:text-4xl">Ready to clear it out?</h2>
          <p className="mt-3 text-white/85 max-w-xl mx-auto">Free, no-obligation quotes. Same-day service available across San Diego County.</p>
          <a href={`tel:${PHONE_TEL}`} className="mt-7 inline-flex items-center gap-2 bg-[#46A01E] hover:bg-[#3A8418] text-white font-display font-extrabold uppercase tracking-wide px-8 py-4 rounded-sm transition-colors">
            <Phone size={20} weight="fill" /> Call/Text {PHONE_DISPLAY}
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
